import { useEffect, useState } from 'react';
import type { AgentSummary } from '../services/agentsAdminService';
import { agentsAdminService } from '../services/agentsAdminService';
import { AgentConfigModal } from './AgentConfigModal';

const STATUS_BADGE: Record<string, string> = {
  active:  'bg-success',
  idle:    'bg-secondary',
  error:   'bg-danger',
  stopped: 'bg-warning text-dark',
  planned: 'bg-light text-dark border',
};

const CATEGORY_META: Record<string, { label: string; icon: string; color: string }> = {
  python_agent:  { label: 'Python',   icon: 'bi-code-slash',    color: '#4e6ef2' },
  claude_persona:{ label: 'Persona',  icon: 'bi-person-badge',  color: '#7c3aed' },
  mcp_tool:      { label: 'MCP',      icon: 'bi-plug',          color: '#0891b2' },
  celery_task:   { label: 'Celery',   icon: 'bi-clock-history', color: '#d97706' },
};

const CATEGORIES = Object.keys(CATEGORY_META) as Array<keyof typeof CATEGORY_META>;

function fmt(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

function AgentCard({
  agent,
  onSelect,
}: {
  agent: AgentSummary;
  onSelect: (a: AgentSummary) => void;
}) {
  const meta = CATEGORY_META[agent.category ?? ''];
  return (
    <div
      className="card h-100 shadow-sm"
      style={{ cursor: 'pointer', borderLeft: `4px solid ${meta?.color ?? '#6c757d'}` }}
      onClick={() => onSelect(agent)}
    >
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="d-flex align-items-center gap-2">
            {meta && <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: '1.1rem' }} />}
            <div>
              <div className="fw-semibold small lh-sm">{agent.agent_name ?? agent.agent_id}</div>
              <div className="text-muted font-monospace" style={{ fontSize: '0.7rem' }}>{agent.agent_id}</div>
            </div>
          </div>
          <span className={`badge ${STATUS_BADGE[agent.status] ?? 'bg-secondary'}`} style={{ fontSize: '0.65rem' }}>
            {agent.status}
          </span>
        </div>

        {agent.description && (
          <p className="text-muted mb-2" style={{ fontSize: '0.78rem', lineHeight: 1.4 }}>
            {agent.description}
          </p>
        )}

        <div className="d-flex gap-1 flex-wrap">
          <span className={`badge ${agent.mode === 'autonomous' ? 'bg-warning text-dark' : 'bg-info text-dark'}`} style={{ fontSize: '0.65rem' }}>
            {agent.mode}
          </span>
          {agent.version && (
            <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>v{agent.version}</span>
          )}
          {agent.trigger_type && (
            <span className="badge bg-light text-dark border" style={{ fontSize: '0.65rem' }}>{agent.trigger_type}</span>
          )}
        </div>

        {(agent.last_action_type || agent.last_action_created_at) && (
          <div className="mt-2 pt-2 border-top">
            <small className="text-muted d-block" style={{ fontSize: '0.7rem' }}>
              {agent.last_action_type ?? '—'} · {fmt(agent.last_action_created_at)}
            </small>
            {agent.last_action_result && (
              <span className={`badge ${agent.last_action_result === 'success' ? 'bg-success' : agent.last_action_result === 'error' ? 'bg-danger' : 'bg-warning text-dark'}`} style={{ fontSize: '0.6rem' }}>
                {agent.last_action_result}
              </span>
            )}
          </div>
        )}

        {agent.children.length > 0 && (
          <div className="mt-2">
            <small className="text-muted" style={{ fontSize: '0.7rem' }}>
              <i className="bi bi-diagram-3 me-1" />
              {agent.children.length} sous-agent{agent.children.length > 1 ? 's' : ''}
            </small>
          </div>
        )}
      </div>
    </div>
  );
}

function CategorySection({
  category,
  agents,
  onSelect,
}: {
  category: string;
  agents: AgentSummary[];
  onSelect: (a: AgentSummary) => void;
}) {
  const meta = CATEGORY_META[category];
  if (agents.length === 0) return null;
  const active = agents.filter(a => a.status === 'active').length;

  return (
    <section className="mb-4">
      <div className="d-flex align-items-center gap-2 mb-3">
        {meta && <i className={`bi ${meta.icon}`} style={{ color: meta.color, fontSize: '1.2rem' }} />}
        <h6 className="mb-0 fw-semibold">{meta?.label ?? category}</h6>
        <span className="badge bg-secondary ms-1">{agents.length}</span>
        {active > 0 && <span className="badge bg-success">{active} actif{active > 1 ? 's' : ''}</span>}
      </div>
      <div className="row row-cols-1 row-cols-md-2 row-cols-xl-3 g-3">
        {agents.map(agent => (
          <div key={agent.agent_id} className="col">
            <AgentCard agent={agent} onSelect={onSelect} />
          </div>
        ))}
      </div>
    </section>
  );
}

export function AgentsPage() {
  const [agents, setAgents] = useState<AgentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selected, setSelected] = useState<AgentSummary | null>(null);
  const [activeTab, setActiveTab] = useState<string>('all');

  async function load() {
    setLoading(true);
    setError(null);
    try {
      setAgents(await agentsAdminService.listAgents());
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Erreur chargement agents');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, []);

  // Aplatir arbre pour le filtre (inclut les enfants dans le comptage total)
  function flatten(list: AgentSummary[]): AgentSummary[] {
    const result: AgentSummary[] = [];
    for (const a of list) {
      result.push(a);
      if (a.children.length > 0) result.push(...flatten(a.children));
    }
    return result;
  }

  const flat = flatten(agents);
  const byCategory = CATEGORIES.reduce<Record<string, AgentSummary[]>>((acc, cat) => {
    acc[cat] = flat.filter(a => a.category === cat);
    return acc;
  }, {});
  const uncategorized = flat.filter(a => !a.category || !CATEGORIES.includes(a.category as typeof CATEGORIES[number]));

  const totalActive = flat.filter(a => a.status === 'active').length;

  const displayedByCategory = activeTab === 'all'
    ? byCategory
    : { [activeTab]: byCategory[activeTab] ?? [] };

  return (
    <div className="container-fluid py-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="mb-0">Agents</h4>
          <small className="text-muted">
            {flat.length} agent{flat.length > 1 ? 's' : ''} · {totalActive} actif{totalActive > 1 ? 's' : ''}
          </small>
        </div>
        <button className="btn btn-sm btn-outline-secondary" onClick={load} disabled={loading}>
          <i className="bi bi-arrow-clockwise me-1" />
          Actualiser
        </button>
      </div>

      {error && <div className="alert alert-danger">{error}</div>}

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border" />
        </div>
      )}

      {!loading && !error && (
        <>
          {/* Onglets par catégorie */}
          <ul className="nav nav-tabs mb-4">
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'all' ? 'active' : ''}`}
                onClick={() => setActiveTab('all')}
              >
                Tous
                <span className="badge bg-secondary ms-1">{flat.length}</span>
              </button>
            </li>
            {CATEGORIES.map(cat => {
              const count = byCategory[cat]?.length ?? 0;
              if (count === 0) return null;
              const meta = CATEGORY_META[cat];
              return (
                <li key={cat} className="nav-item">
                  <button
                    className={`nav-link ${activeTab === cat ? 'active' : ''}`}
                    onClick={() => setActiveTab(cat)}
                  >
                    <i className={`bi ${meta.icon} me-1`} />
                    {meta.label}
                    <span className="badge bg-secondary ms-1">{count}</span>
                  </button>
                </li>
              );
            })}
          </ul>

          {/* Grille par catégorie */}
          {Object.entries(displayedByCategory).map(([cat, list]) => (
            <CategorySection
              key={cat}
              category={cat}
              agents={list}
              onSelect={setSelected}
            />
          ))}

          {uncategorized.length > 0 && activeTab === 'all' && (
            <CategorySection
              category="Autres"
              agents={uncategorized}
              onSelect={setSelected}
            />
          )}

          {flat.length === 0 && (
            <div className="alert alert-light border text-center">
              <i className="bi bi-cpu me-2" />
              Aucun agent enregistré. Les agents Python s'enregistrent automatiquement au démarrage.
            </div>
          )}
        </>
      )}

      {selected && (
        <AgentConfigModal
          agent={selected}
          onClose={() => setSelected(null)}
          onSelectAgent={setSelected}
        />
      )}
    </div>
  );
}
